import { computed, watch, toValue, type MaybeRefOrGetter } from 'vue';
import { storeToRefs } from 'pinia';
import { type Bookmark } from '@ilteducation/annotations-types';
import useAnnotationsStore from './annotations-store';

const useBookmarks = (
  contentId: MaybeRefOrGetter<string | null | undefined>,
  profileId?: MaybeRefOrGetter<string | undefined>,
  options = { autoLoad: true },
) => {
  const store = useAnnotationsStore();
  const { create, update, remove, isFetching } = store;
  const { annotations } = storeToRefs(store);

  // Reactive filtering. Ensures that whenever the contentId (Ref or Getter) changes, the bookmarks list updates automatically.
  const bookmarks = computed(() => {
    const id = toValue(contentId);
    return Object.values(annotations.value).filter((a): a is Bookmark => a.type === 'bookmark' && a.contentId === id);
  });

  // Reactive loading state specific to these types/contentId
  const isLoading = computed(() => {
    const id = toValue(contentId);
    return isFetching([`highlight:${id}`, `underline:${id}`]);
  });

  const load = async () => {
    const id = toValue(contentId);
    const pId = toValue(profileId);
    if (!id) {
      return Promise.reject();
    }
    return Promise.all([store.load('bookmark', id, pId)]);
  };

  watch(
    () => toValue(contentId),
    (newId) => {
      if (options.autoLoad && newId && bookmarks.value.length === 0) {
        load();
      }
    },
    { immediate: true },
  );

  return {
    bookmarks,
    isLoading,
    load,
    create,
    update,
    remove,
  };
};

export default useBookmarks;
